import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { i18n } from 'shared/i18n'
import { changeDaysOffset } from '../../reducers/ui'
import { readPreference, writePreference, switchAllowed } from './preference'

export const BookingScreensContext = createContext(null)
const storage = () => { try { return window.localStorage } catch { return null } }

export const BookingScreensProvider = ({ children }) => {
    const [enabled, setEnabled] = useState(() => readPreference(storage()))
    const guards = useRef(new Set())
    const view = useRef(null)
    const handover = useRef(null)
    const pendingMutations = useRef(0)
    const api = useMemo(() => ({
        registerGuard: getter => { guards.current.add(getter); return () => guards.current.delete(getter) },
        getGuardStates: () => [...guards.current].map(get => get()).concat({ pending: pendingMutations.current > 0 }),
        registerView: getter => { view.current = getter; return () => { if (view.current === getter) view.current = null } },
        trackMutation: async action => {
            pendingMutations.current++
            try { return await action() } finally { pendingMutations.current-- }
        },
        takeView: (siteId, screen) => {
            const saved = handover.current
            handover.current = null
            return saved?.siteId === siteId && saved.screen === screen ? saved : null
        },
    }), [])
    const toggle = () => {
        const currentGuards = api.getGuardStates()
        if (currentGuards.some(guard => guard.pending)) {
            window.alert(i18n().t('Wait for the current operation to finish before switching screens.'))
            return
        }
        if (!switchAllowed(currentGuards, () => window.confirm(i18n().t('Discard unsaved booking edits and switch screens?')))) return
        handover.current = view.current?.() || null
        writePreference(storage(), !enabled)
        setEnabled(!enabled)
    }
    return <BookingScreensContext.Provider value={{ ...api, enabled, toggle }}>{children}</BookingScreensContext.Provider>
}

// Getters make rapid clicks safe without waiting for a React render.
export const useBookingScreenGuard = (state) => {
    const context = useContext(BookingScreensContext)
    const latest = useRef(state)
    latest.current = state
    useEffect(() => context?.registerGuard(() => typeof latest.current === 'function' ? latest.current() : latest.current), [context?.registerGuard])
}

export const BookingScreenBoundary = ({ site, screen, classic: Classic, experimental: Experimental, ...props }) => {
    const context = useContext(BookingScreensContext)
    const dispatch = useDispatch()
    const initialView = useMemo(() => context.takeView(site.id, screen), [context.enabled, site.id, screen])
    useLayoutEffect(() => {
        if (initialView) dispatch(changeDaysOffset(initialView.daysOffset))
    }, [initialView, dispatch])
    const Page = context.enabled ? Experimental : Classic
    return <Page key={`${site.id}-${context.enabled}`} site={site} initialView={initialView} bookingScreens={context} {...props} />
}

export const ExperimentalBookingControl = () => {
    const { enabled, toggle } = useContext(BookingScreensContext)
    return <button type="button" className="link-button dropdown-link-button experimental-booking-control"
        title={i18n().t('You can return to classic screens at any time.')} onClick={toggle}>
        {i18n().t(enabled ? 'Turn off experimental booking screens' : 'Turn on experimental booking screens')}
    </button>
}
